import {
  isCordovaApp,
  isOnline,
  cordovaFetchBooksAndChapters,
  cordovaFetchChapter,
  cordovaFetchBibles,
  cordovaFetchBible
} from "../utils/cordovaUtils";

import { Passage } from "./typedefs/Passage";
import { Bible, Chapter, FumsMeta } from "./typedefs/Bible";
import { get } from "./utils";
import { SearchResults } from "./typedefs/SearchResults";
import { bibleStore } from "../storage/stores";
import { storageFetch } from "../storage/fetchers";

const {
  REACT_APP_BIBLE_API = "",
  REACT_APP_BIBLE_API_KEY = "",
  REACT_APP_FUMS_VERSION = ""
} = process.env;
const baseURL = REACT_APP_BIBLE_API || "https://v2.api.bible/";
const fumsVersion = REACT_APP_FUMS_VERSION; 

type BibleType = 'text' | 'audio';

export const headers = new Headers({
  "Content-Type": "application/json",
  "api-key": REACT_APP_BIBLE_API_KEY
});

// Fetch Books
export const getBooksUrl = (bibleId: string, bibleType: BibleType = 'text') => {
  return `${baseURL}${
    bibleType === 'audio' ? 'audio-bibles' : 'bibles'}/${bibleId}/books?include-chapters=true`;
}

export const fetchBooksAndChapters = async ({
  bibleId,
  bibleType = 'text'
}: {
  bibleId: string;
  bibleType?: BibleType;
}) => {
  if (isCordovaApp) return await cordovaFetchBooksAndChapters(bibleId);
  return await storageFetch(getBooksUrl(bibleId, bibleType), { headers }, bibleStore);
};

// Fetch Chapter
export const getChapterUrl = (
  bibleId: string,
  chapterId: string,
  parallels?: string[]
) => {
  let queryString = `content-type=json&include-verse-spans=true&include-notes=true`;
  if (fumsVersion) queryString += `&fums-version=${fumsVersion}`;
  if (parallels && parallels.length)
    queryString += `&parallels=${parallels.join(",")}`;
  return `${baseURL}bibles/${bibleId}/chapters/${chapterId}?${queryString}`;
};

export const fetchChapter = async ({
  bibleId,
  chapterId,
  parallels = []
}: {
  bibleId: string;
  chapterId: string;
  parallels: string[];
}) => {
  if (isCordovaApp && (parallels.length < 1 || !isOnline())) {
    return await cordovaFetchChapter(bibleId, chapterId, parallels);
  }
  return await storageFetch<{ data: Chapter; meta: FumsMeta }>(
    getChapterUrl(bibleId, chapterId, parallels),
    { headers },
    bibleStore
  ).then(res => {
    if (!res.data) return;

    const chapterWithFumsId = Object.assign(res.data, {
      fumsToken: res.meta.fumsToken
    });
    if (chapterWithFumsId.parallels) {
      chapterWithFumsId.parallels.map(
        (parallel: Omit<Chapter, "fumsToken">): Chapter =>
          Object.assign(parallel, { fumsToken: res.meta.fumsToken })
      );
    }
    return chapterWithFumsId;
  });
};

export const fetchAudioChapter = ({
  bibleId,
  chapterId
}: {
  bibleId: string;
  chapterId: string;
}) =>
  get<{ data: any }>(
    `${baseURL}audio-bibles/${bibleId}/chapters/${chapterId}?include-timecodes=true&fums-version=3`,
    {
      headers
    }
  ).then(res => {
    if (!res.data) return;
    return Object.assign(res.data, { fumsToken: (res as any).meta?.fumsToken });
  });

// Fetch Bibles
export const getBiblesUrl = (bibleIds: string[], fetchAllBibles?: boolean) => {
  if (fetchAllBibles) return `${baseURL}bibles`;
  return `${baseURL}bibles?ids=${bibleIds.join(",")}`;
};

export const fetchBibles = (
  bibleIds: string[],
  fetchAllBibles?: boolean
): Promise<{ data: Bible[] }> => {
  if (isCordovaApp) return cordovaFetchBibles(bibleIds);
  return get(getBiblesUrl(bibleIds, fetchAllBibles), { headers });
};

// Fetch Passage
export const fetchPassage = ({
  bibleId,
  passageId
}: {
  bibleId: string;
  passageId: string;
}): Promise<Passage> =>
  get<{ data: Omit<Passage, "fumsToken">; meta: FumsMeta }>(
    `${baseURL}bibles/${bibleId}/passages/${passageId}?content-type=text&include-verse-numbers=false&include-titles=false${
      fumsVersion ? `&fums-version=${fumsVersion}` : ""
    }`,
    {
      headers
    }
  ).then(res => Object.assign(res.data, { fumsToken: res.meta.fumsToken }));

export const search = ({
  bibleId,
  query,
  offset = 0,
  limit = 10
}: {
  bibleId: string;
  query: string;
  offset?: number;
  limit?: number;
}) =>
  get<SearchResults>(
    `${baseURL}bibles/${bibleId}/search?query=${query}&offset=${offset}&limit=${limit}&sort=canonical&fuzziness=0${
      fumsVersion ? `&fums-version=${fumsVersion}` : ""
    }`,
    { headers }
  );

// Fetch Bible
export const getBibleUrl = (bibleId: string) => `${baseURL}bibles/${bibleId}`;

export const fetchBible = async ({ bibleId }: { bibleId: string }) => {
  if (isCordovaApp) return cordovaFetchBible(bibleId).then(res => res);
  return storageFetch<{ data: Bible }>(
    getBibleUrl(bibleId),
    { headers },
    bibleStore
  ).then(res => res.data);
};
